import { Activity, useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
// import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { NavbarCleaner } from '@/components/layout/navbar/navbar.cleaner';
import { VISUAL } from '@/features/cleaner/apply-cleaner/constant';
import { userOnboardingCompletionStore } from '@/stores/user-onboarding-completion';
import { userAvailabilityStore } from '@/stores/availability-store';
// import { useCounterStore } from '@/stores/counter-store';
// import { type RegistrationFormValues } from '@/features/cleaner/apply-cleaner/schemas';
import { PersonalInfoPage } from '@/features/cleaner/oboarding/PersonalInfo';
import { StepsIndicator } from '@/features/cleaner/apply-cleaner/steps/StepsIndicator';
import { AvailabilitySection } from '@/features/cleaner/work-setup/AvailabilitySection';
import SetupDeposit from '@/features/cleaner/apply-cleaner/steps/SetupDeposit';
import { useAuth } from '@/context/auth-context';
import { LoadingScreen } from '@/components/ui/loading-screen';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const availabilityCount = userAvailabilityStore(
    state => state.availabilityCount
  );
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(true);
  const availabilityForm = useForm();
  const applicationCompletion = userOnboardingCompletionStore(
    s => s.applicationCompletion
  );
  const setCurrentStep = userOnboardingCompletionStore(s => s.setCurrentStep);

  const {
    isCompleteWorksetup,
    isCompleteProfile,
    isCompleteCleaningSuppplies,
    isCompleteAvailabilitySetup,
    isStripeSetup,
    currentStep,
  } = applicationCompletion;

  const shouldShowPersonalInfo =
    !applicationCompletion ||
    !isCompleteWorksetup ||
    !isCompleteProfile ||
    currentStep === 2;

  const shouldShowAvailabilitySection =
    (!!applicationCompletion &&
      isCompleteProfile &&
      isCompleteWorksetup &&
      isCompleteCleaningSuppplies &&
      !isCompleteAvailabilitySetup &&
      currentStep === 3) ||
    currentStep === 3;

  const shouldShowSetupDeposit =
    (isCompleteProfile &&
      isCompleteWorksetup &&
      isCompleteCleaningSuppplies &&
      isCompleteAvailabilitySetup &&
      !isStripeSetup &&
      currentStep === 4) ||
    currentStep === 4;

  const isOnboardingFullyComplete =
    isCompleteProfile &&
    isCompleteWorksetup &&
    isCompleteCleaningSuppplies &&
    isCompleteAvailabilitySetup &&
    isStripeSetup;

  useEffect(() => {
    // scroll to the top of the page
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/home', replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isOnboardingFullyComplete && isAuthenticated) {
      navigate({ to: '/cleaner/dashboard', replace: true });
    }
  }, [isOnboardingFullyComplete, isAuthenticated, navigate]);

  if (isOnboardingFullyComplete && isAuthenticated) {
    return (
      <>
        <NavbarCleaner />
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
          <LoadingScreen
            message="Taking you to your dashboard..."
            animationType="spinner"
          />
        </div>
      </>
    );
  }

  return (
    <>
      <NavbarCleaner />
      <div className="flex flex-col items-center min-h-screen gap-4 p-4 py-50">
        <div className="w-full max-w-4xl">
          <StepsIndicator />
          <Activity
            mode={shouldShowPersonalInfo ? VISUAL.VISIBLE : VISUAL.HIDDEN}
          >
            <PersonalInfoPage />
          </Activity>

          <Activity
            mode={
              shouldShowAvailabilitySection ? VISUAL.VISIBLE : VISUAL.HIDDEN
            }
          >
            <Form {...availabilityForm}>
              <form>
                <AvailabilitySection
                  onLoadingChange={setIsAvailabilityLoading}
                />
                <div className="mt-6 flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    className="bg-orange-600 text-white"
                    id="next-button-step-3"
                    disabled={isAvailabilityLoading || availabilityCount <= 0}
                    onClick={() => setCurrentStep(4)}
                  >
                    Next
                  </Button>
                </div>
              </form>
            </Form>
          </Activity>

          <Activity
            mode={shouldShowSetupDeposit ? VISUAL.VISIBLE : VISUAL.HIDDEN}
          >
            <div id="setup-deposit-section">
              <SetupDeposit />
              <div className="mt-6 flex items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(3)}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  className="bg-orange-600 text-white"
                  onClick={() => setCurrentStep(5)}
                >
                  Continue
                </Button>
              </div>
            </div>
          </Activity>
        </div>
      </div>
    </>
  );
}
